<template>
  <div
    class="relative rounded-[2px]"
    :style="{
      border: props.border ? '1px solid #000' : '',
      borderColor: borderColor,
      width: size + 'px',
      height: size + 'px'
    }"
    :class="{
      '!cursor-not-allowed !opacity-30': disabled
    }"
    @drop.stop="handleDrop"
    @mousemove="() => (hover = true)"
    @mouseleave="() => (hover = false)"
  >
    <!-- 存在图片时 -->
    <a-popover
      v-if="imgBase64 || showPreviewIfEmpty"
      v-model:visible="showPreview"
      :trigger="'click'"
      overlay-class-name="imagepicker-preview"
      placement="left"
    >
      <img
        v-if="imgBase64"
        class="object-contain w-full h-full p-[1px] cursor-pointer"
        :class="{
          '!cursor-not-allowed': disabled
        }"
        :src="imgBase64"
        @dragstart="handleImageDrag"
        @click.stop="() => !allowPreview && !disabled && pickImageFile()"
      />
      <template v-if="!imgBase64">
        <div
          class="h-full w-full flex justify-center items-center cursor-pointer"
          :class="{
            '!cursor-not-allowed': disabled
          }"
        >
          <icon
            icon="upload.svg"
            :width="iconSize"
            :height="iconSize"
            fill="none"
          />
        </div>
      </template>
      <!-- 预览区 -->
      <template v-if="allowPreview && !disabled" #content>
        <div class="flex items-center w-[248px] h-12 px-3">
          <div class="flex-1 text-[16px] text-white/70 text-center">
            <div>
              预览
              <span v-if="imgRef?.naturalWidth">
                （{{ imgRef?.naturalWidth }}*{{ imgRef?.naturalHeight }}）
              </span>
            </div>
          </div>
          <icon
            icon="delete2.svg"
            class="cursor-pointer"
            :width="14"
            :height="14"
            fill="none"
            @click.stop="() => (showPreview = false)"
          />
        </div>
        <!-- 图片 -->
        <div
          class="relative group w-[248px] h-[248px] flex items-center justify-center p-4 bg-black"
          :style="{
            backgroundImage: `linear-gradient(45deg, #ccc 25%, transparent 0, transparent 75%, #ccc 0 ), linear-gradient(45deg, #ccc 25%, #fff 0, #fff 75%, #ccc 0)`,
            backgroundPosition: '0 0, 10px 10px',
            backgroundSize: '20px 20px'
          }"
        >
          <img
            v-if="img"
            ref="imgRef"
            :src="img"
            class="w-full h-full object-contain"
          />
          <div
            class="absolute inset-0 flex opacity-0 group-hover:opacity-100 items-center justify-center bg-black/40 transition-all flex-col"
            :class="{
              '!opacity-100': !img
            }"
          >
            <a-button
              type="primary"
              @click.stop="() => !disabled && pickImageFile()"
            >
              修改图片
            </a-button>
            <a-button
              v-if="autoGenerate"
              type="primary"
              class="mt-2.5"
              @click.stop="() => !disabled && emit('auto-generate')"
            >
              自动生成
            </a-button>
          </div>
        </div>
        <!-- actions -->
        <div class="py-1">
          <!-- 旋转 -->
          <div
            v-if="actions.includes('rotate')"
            class="flex items-center h-12 px-6"
          >
            <span class="flex-1 text-[14px] text-white/70">旋转</span>
            <div class="flex items-center gap-4">
              <a-tooltip title="向左旋转90°">
                <icon
                  icon="rotate-back-90.svg"
                  class="cursor-pointer"
                  :width="25"
                  :height="25"
                  fill="none"
                  @click.stop="() => rotate(false)"
                />
              </a-tooltip>
              <a-tooltip title="向右旋转90°">
                <icon
                  icon="rotate-90.svg"
                  class="cursor-pointer"
                  :width="25"
                  :height="25"
                  fill="none"
                  @click.stop="() => rotate(true)"
                />
              </a-tooltip>
            </div>
          </div>
          <!-- 透明度 -->
          <div
            v-if="actions.includes('alpha')"
            class="flex items-center h-12 px-6"
          >
            <span class="flex-1 text-[14px] text-white/70">透明度</span>
            <div class="flex items-center bg-yello1w-50 flex-[2.5]">
              <a-slider class="w-full" :min="0" :max="100" :step="1" />
              <span>ww</span>
            </div>
          </div>
        </div>
      </template>
    </a-popover>
    <!-- 不存在图片时 显示添加按钮 -->
    <div
      v-if="!imgBase64 && !showPreviewIfEmpty"
      class="h-full w-full flex justify-center items-center cursor-pointer"
      :class="{
        '!cursor-not-allowed': disabled
      }"
    >
      <icon
        icon="upload.svg"
        :width="iconSize"
        :height="iconSize"
        fill="none"
        @click.stop="() => !disabled && pickImageFile()"
      />
    </div>
    <!-- 图片删除icon -->
    <div
      v-if="deletable && imgBase64 && !disabled && (hover || showDeleteAlways)"
      class="absolute top-[-11px] right-[-5px] cursor-pointer"
      @click.stop="onDelete"
    >
      <icon icon="delete.svg" :width="10" :height="10" fill="none" />
    </div>
  </div>
</template>
<script lang="ts" setup>
import { useWatchfaceImageValid } from '@/hooks';
import { withTempFile } from '@/hooks/use-temp-file';
import { pickFile } from '@/utils/fs/pick-file.utils';
import { rotateImg90deg } from '@/utils/transform-image';
import { PropType, ref, watch } from 'vue';

const props = defineProps({
  img: {
    type: String,
    default: ''
  },
  size: {
    type: Number,
    default: 24
  },
  iconSize: {
    type: Number,
    default: 14
  },
  border: {
    type: Boolean,
    default: true
  },
  borderColor: {
    type: String,
    default: '#000000'
  },
  deletable: {
    type: Boolean,
    default: true
  },
  showDeleteAlways: {
    type: Boolean,
    default: false
  },
  disabled: {
    type: Boolean,
    default: false
  },
  dragData: {
    type: Object as PropType<Record<string, string>>,
    default: () => ({})
  },
  allowPreview: {
    type: Boolean,
    default: true
  },
  autoGenerate: {
    type: Boolean,
    default: false
  },
  showPreviewIfEmpty: {
    type: Boolean,
    default: false
  },
  actions: {
    type: Array as PropType<('rotate' | 'alpha')[]>,
    default: () => ['rotate']
  }
});
const emit = defineEmits<{
  (e: 'change', img: string): void;
  (e: 'delete'): void;
  (e: 'drop', event: DragEvent): void;
  (e: 'auto-generate'): void;
}>();

const imgBase64 = ref('');
const imgRef = ref<HTMLImageElement>();

watch(
  () => props.img,
  (newVal) => {
    imgBase64.value = newVal;
  },
  {
    immediate: true
  }
);

const { validImageSize } = useWatchfaceImageValid();
const pickImageFile = async () => {
  const filename = await pickFile({ extensions: ['png'], multi: false }).then(
    (r) => r?.[0]
  );
  if (!filename) return;
  // 校验
  if (!(await validImageSize(filename))) return false;
  emit('change', filename);
};

const onDelete = () => {
  emit('delete');
};

const handleDrop = async (e: DragEvent) => {
  const files = e.dataTransfer?.files;
  if (
    files &&
    files.length === 1 &&
    files[0].type === 'image/png' &&
    files[0].path &&
    (await validImageSize(files[0].path))
  ) {
    emit('change', files[0].path);
    e.stopImmediatePropagation();
    e.stopPropagation();
  } else emit('drop', e);
};

const handleImageDrag = (e: DragEvent) => {
  if (!e.dataTransfer) return;
  e.dataTransfer.setData('image', props.img);
  for (const key in props.dragData)
    e.dataTransfer.setData(key, props.dragData[key].toString());
};

const hover = ref(false);

const showPreview = ref(false);

const rotate = async (clockwise: boolean) => {
  if (!props.img) return;
  const img = await rotateImg90deg(props.img, clockwise);
  withTempFile(img, async (filename) => emit('change', filename), false);
};
</script>
<style lang="less">
.imagepicker-preview {
  .ant-popover-arrow-content {
    visibility: hidden;
  }
  .ant-popover-inner {
    border-radius: 0;
  }
  .ant-popover-inner-content {
    padding: 0;
  }
}
</style>
